import { forwardRef, useState, type FormEvent, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../base/Button';
import type { BaseComponentProps } from '../../types';

export interface NewsletterFormProps extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'>, BaseComponentProps {
  /**
   * Callback submit
   */
  onSubmit?: (email: string) => void;
  /**
   * Placeholder
   */
  placeholder?: string;
  /**
   * Testo button
   */
  buttonText?: string;
  /**
   * Loading state
   */
  loading?: boolean;
}

/**
 * NewsletterForm Component
 * 
 * Form per iscrizione newsletter.
 * 
 * @example
 * ```tsx
 * <NewsletterForm
 *   onSubmit={(email) => console.log(email)}
 *   placeholder="Il tuo email"
 *   buttonText="Iscriviti"
 * />
 * ```
 */
export const NewsletterForm = forwardRef<HTMLFormElement, NewsletterFormProps>(
  (
    {
      className,
      onSubmit,
      placeholder = 'La tua email...',
      buttonText = 'Iscriviti',
      loading = false,
      ...props
    },
    ref
  ) => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (email && onSubmit) {
        onSubmit(email);
        setEmail('');
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      }
    };

    return (
      <form
        ref={ref}
        onSubmit={handleSubmit}
        className={cn('w-full max-w-md mx-auto', className)}
        {...props}
      >
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            required
            className={cn(
              'flex-1 px-4 py-2 rounded-md border transition-colors',
              'bg-base-100 text-base-content placeholder:text-base-content/40',
              'border-base-300 focus:border-primary focus:ring-2 focus:ring-primary focus:outline-none'
            )}
          />
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading || !email}
          >
            {buttonText}
          </Button>
        </div>

        {submitted && (
          <p className="mt-2 text-sm text-success">
            ✓ Grazie per l'iscrizione!
          </p>
        )}
      </form>
    );
  }
);

NewsletterForm.displayName = 'NewsletterForm';

